import { sortBy } from 'lodash';
import React, { useMemo } from 'react';
import ButtonModel, {
  GenericButtonModel,
  OptInButtonConfig,
} from '@mm/state/model/ButtonModel';
import WidgetModel from '@mm/state/model/WidgetModel';
import { useButtonHandlers } from '../../helpers/widget-helpers/use-button-handlers';
import {
  SortableContainer,
  SortableElement,
} from '../../lib/react-sortable-hoc';
import { ActivePathType } from '../common/ActivePathSelection/ActivePathSelection';
import { OptInButton } from './OptInButton';

type Props = {
  widget: WidgetModel;
  openedButtonId: number | undefined;
  setOpenedButtonId: (openedButtonId: number | undefined) => void;
  setActivePathType: (activePathType: ActivePathType) => void;
  setActivePageId: (id: number | undefined) => void;
};

const SortableOptInButton = SortableElement(OptInButton);

const ButtonsContainer = SortableContainer(
  ({ children, ...props }: { [key: string]: any }) => (
    <div {...props}>{children}</div>
  ),
);

export const OptInButtonStack = (props: Props) => {
  const {
    widget,
    openedButtonId,
    setOpenedButtonId,
    setActivePathType,
    setActivePageId,
  } = props;
  const buttonHandlers = useButtonHandlers();
  const buttons = useMemo(
    (): ButtonModel[] => sortBy(widget.buttons, ['sortOrder']),
    [widget.buttons],
  ) as GenericButtonModel<OptInButtonConfig>[];

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    if (oldIndex === newIndex) return;

    const newButtons = [...buttons];

    const { sortOrder: newSortOrder } = newButtons[newIndex];
    const targetButton = { ...newButtons[oldIndex] };

    buttonHandlers.updateButton(widget, {
      ...targetButton,
      sortOrder: newSortOrder,
    });

    if (oldIndex < newIndex) {
      for (let i = oldIndex + 1; i <= newIndex; i += 1) {
        const button = newButtons[i];

        buttonHandlers.updateButton(widget, {
          ...button,
          sortOrder: button.sortOrder - 1,
        });
      }
    }

    if (oldIndex > newIndex) {
      for (let i = newIndex; i < oldIndex; i += 1) {
        const button = newButtons[i];

        buttonHandlers.updateButton(widget, {
          ...button,
          sortOrder: button.sortOrder + 1,
        });
      }
    }
  };

  return (
    <ButtonsContainer
      onSortEnd={onSortEnd}
      lockAxis="y"
      distance={15}
      lockToContainerEdges
      lockOffset={0}
      className="OptIn-buttons-list"
    >
      {buttons.map((button, index: number) => (
        <SortableOptInButton
          index={index}
          button={button}
          deleteable={button.config?.kind === 'text'}
          widget={widget}
          key={button.id}
          openedButtonId={openedButtonId}
          setOpenedButtonId={setOpenedButtonId}
          setActivePathType={setActivePathType}
          setActivePageId={setActivePageId}
        />
      ))}
    </ButtonsContainer>
  );
};
